import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api, { apiHelpers } from '../../services/api';
import {
  EnvelopeIcon,
  BuildingOfficeIcon,
  UserIcon,
  ArrowRightIcon,
  CheckIcon,
  XMarkIcon,
  ClockIcon,
  GiftIcon,
  TicketIcon,
  SparklesIcon,
  GlobeAltIcon,
  ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { TIMEZONES, TIMEZONE_GROUPS } from '../../constants/timezones';

// Industry options
const INDUSTRIES = [
  { value: '', label: 'Select your industry' },
  { value: 'technology', label: 'Technology' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'retail', label: 'Retail & E-commerce' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'education', label: 'Education' },
  { value: 'media', label: 'Media & Entertainment' },
  { value: 'consulting', label: 'Consulting & Services' },
  { value: 'other', label: 'Other' }
];

// Format price for display
const formatPrice = (pricing, currencyCode = 'USD') => {
  const price = pricing?.find(p => p.currencyCode === currencyCode) || pricing?.[0];
  if (!price) return 'Free';
  if (price.amount === 0) return 'Free';
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price.amount) + '/mo';
};

// Plan card component
function PlanCard({ plan, isSelected, onSelect }) {
  const features = plan.features || {};
  
  return (
    <div
      onClick={() => onSelect(plan.slug)}
      className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${
        isSelected
          ? 'border-primary-500 bg-primary-500/10'
          : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
      }`}
    >
      {isSelected && (
        <div className="absolute top-3 right-3 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
          <CheckIcon className="w-4 h-4 text-white" />
        </div>
      )}
      
      <h3 className="font-semibold text-surface-100 text-lg">{plan.name}</h3>
      <p className="text-2xl font-bold text-primary-400 mt-1">
        {formatPrice(plan.pricing)}
      </p>
      
      <ul className="mt-3 space-y-1.5 text-sm text-surface-400">
        <li className="flex items-center gap-2">
          <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span>{features.userSeats || 1} user seat{(features.userSeats || 1) > 1 ? 's' : ''}</span>
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span>{features.storageGB || 0} GB storage</span>
        </li>
        <li className="flex items-center gap-2">
          <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
          <span>
            {features.dashboardLimit === -1 
              ? 'Unlimited dashboards' 
              : `${features.dashboardLimit || 1} dashboard${(features.dashboardLimit || 1) > 1 ? 's' : ''}`}
          </span>
        </li>
        {(features.monthlyCredits > 0 || features.oneTimeCredits > 0) && (
          <li className="flex items-center gap-2">
            <CheckIcon className="w-4 h-4 text-emerald-400 flex-shrink-0" />
            <span>
              {features.monthlyCredits > 0 
                ? `${features.monthlyCredits.toLocaleString()} AI credits/mo`
                : `${(features.oneTimeCredits || 0).toLocaleString()} AI credits`}
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [registrationEnabled, setRegistrationEnabled] = useState(false);
  const [betaMode, setBetaMode] = useState(false);
  const [plans, setPlans] = useState([]);
  const [step, setStep] = useState(1); // 1: Company info, 2: Plan selection
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Referral code state
  const [referralCode, setReferralCode] = useState('');
  const [referralValidation, setReferralValidation] = useState(null); // { valid, message, referrer }
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);
  
  // Invite code state (for beta)
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeValidation, setInviteCodeValidation] = useState(null);
  const [isValidatingInviteCode, setIsValidatingInviteCode] = useState(false);
  
  // Region state
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(true);
  const [wantsSuggestRegion, setWantsSuggestRegion] = useState(false);
  const [suggestedRegion, setSuggestedRegion] = useState('');
  
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    timezone: 'UTC',
    dataLocation: '',
    adminEmail: '',
    adminFirstName: '',
    adminLastName: '',
    planSlug: ''
  });

  // Check if registration is enabled and fetch plans and regions
  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const [statusResponse, plansResponse, regionsResponse] = await Promise.all([
          api.get('/auth/registration-status'),
          api.get('/auth/plans'),
          apiHelpers.getAvailableRegions().catch(() => ({ data: { regions: [] } }))
        ]);
        
        setRegistrationEnabled(statusResponse.data.registrationEnabled);
        setBetaMode(statusResponse.data.betaMode || false);
        setPlans(plansResponse.data.plans || []);
        
        // Set regions and default
        const availableRegions = regionsResponse.data.regions || [];
        setRegions(availableRegions);
        setLoadingRegions(false);
        
        // Set default region
        const defaultRegion = availableRegions.find(r => r.isDefault) || availableRegions[0];
        
        // Pre-select first plan if available (but not in beta mode - plan is forced to Basic)
        if (plansResponse.data.plans?.length > 0 && !statusResponse.data.betaMode) {
          setFormData(prev => ({
            ...prev,
            planSlug: plansResponse.data.plans[0].slug,
            dataLocation: defaultRegion?.regionId || ''
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            dataLocation: defaultRegion?.regionId || ''
          }));
        }
        
        // Check for referral code in URL
        const refCode = searchParams.get('ref');
        if (refCode) {
          setReferralCode(refCode);
          validateReferralCode(refCode);
        }
        
        // Check for invite code in URL
        const invCode = searchParams.get('invite');
        if (invCode) {
          setInviteCode(invCode);
          validateInviteCode(invCode);
        }
      } catch (error) {
        console.error('Error checking registration status:', error);
        toast.error('Unable to load registration page');
      } finally {
        setIsCheckingStatus(false);
        setLoadingRegions(false);
      }
    };
    
    checkRegistrationStatus();
  }, [searchParams]);
  
  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 5) {
      setReferralValidation(null);
      return;
    }
    
    setIsValidatingReferral(true);
    try {
      const response = await apiHelpers.validateReferralCode(code);
      setReferralValidation(response.data);
    } catch (error) {
      setReferralValidation({ valid: false, message: 'Invalid referral code' });
    } finally {
      setIsValidatingReferral(false);
    }
  };
  
  // Validate invite code (for beta)
  const validateInviteCode = async (code, email = null) => {
    if (!code || code.length < 5) {
      setInviteCodeValidation(null);
      return;
    }
    
    setIsValidatingInviteCode(true);
    try {
      const response = await api.post('/auth/validate-invite-code', { 
        code, 
        email: email || formData.adminEmail 
      });
      setInviteCodeValidation(response.data);
    } catch (error) {
      setInviteCodeValidation({ valid: false, message: 'Invalid invite code' });
    } finally {
      setIsValidatingInviteCode(false);
    }
  };
  
  // Debounced referral code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (referralCode && referralCode.length >= 5) {
        validateReferralCode(referralCode);
      } else {
        setReferralValidation(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [referralCode]);
  
  // Debounced invite code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inviteCode && inviteCode.length >= 5) {
        validateInviteCode(inviteCode, formData.adminEmail);
      } else {
        setInviteCodeValidation(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [inviteCode, formData.adminEmail]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateStep1 = () => {
    if (!formData.companyName.trim()) {
      toast.error('Company name is required');
      return false;
    }
    if (!formData.adminFirstName.trim() || !formData.adminLastName.trim()) {
      toast.error('Admin name is required');
      return false;
    }
    if (!formData.adminEmail.trim()) {
      toast.error('Admin email is required');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.adminEmail)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    // Beta mode requires valid invite code
    if (betaMode) {
      if (!inviteCode.trim()) {
        toast.error('Invite code is required to register during beta');
        return false;
      }
      if (!inviteCodeValidation?.valid) {
        toast.error('Please enter a valid invite code');
        return false;
      }
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      // In beta mode, skip plan selection (forced to Basic)
      if (betaMode && inviteCodeValidation?.valid) {
        setStep(2);
      } else if (!betaMode) {
        setStep(2);
      }
    }
  };

  const handlePrevStep = () => {
    setStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // In beta mode, plan selection is skipped (forced to Basic)
    if (!betaMode && !formData.planSlug) {
      toast.error('Please select a plan');
      return;
    }
    
    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Build submit data
      const submitData = {
        ...formData,
        ...(referralValidation?.valid && referralCode ? { referralCode } : {}),
        ...(betaMode && inviteCodeValidation?.valid ? { inviteCode } : {}),
        ...(wantsSuggestRegion && suggestedRegion.trim() ? { suggestedRegion: suggestedRegion.trim() } : {})
      };
      
      const response = await api.post('/auth/register-company', submitData);
      
      let successMessage = response.data.message || 'Registration successful! Check your email.';
      if (response.data.referral?.applied) {
        successMessage += ` Referral bonus of ${response.data.referral.pendingReward} credits pending!`;
      }
      
      toast.success(successMessage);
      
      // Redirect to login with success message
      navigate('/login', { 
        state: { 
          registrationSuccess: true,
          message: 'Please check your email to verify your account and set your password.',
          referral: response.data.referral,
          isBetaUser: response.data.company?.isBetaUser
        }
      });
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (isCheckingStatus) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show disabled message if registration is not enabled
  if (!registrationEnabled) {
    return (
      <div className="animate-slide-up text-center">
        <div className="w-16 h-16 bg-surface-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <XMarkIcon className="w-8 h-8 text-surface-400" />
        </div>
        <h2 className="text-2xl font-display font-bold text-white mb-2">
          Registration Closed
        </h2>
        <p className="text-surface-400 mb-6">
          We're currently in private beta and not accepting new registrations.
          Please contact us if you'd like to request access.
        </p>
        <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors">
          Already have an account? Sign in
        </Link>
      </div>
    );
  }

  return (
    <div className="animate-slide-up">
      <h2 className="text-2xl font-display font-bold text-white mb-2">
        {step === 1 ? 'Create your account' : 'Choose your plan'}
      </h2>
      <p className="text-surface-400 mb-6">
        {step === 1 
          ? 'Set up your company and admin account'
          : 'Select the plan that best fits your needs'}
      </p>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 mb-6">
        <div className={`flex-1 h-1 rounded-full ${step >= 1 ? 'bg-primary-500' : 'bg-surface-700'}`} />
        <div className={`flex-1 h-1 rounded-full ${step >= 2 ? 'bg-primary-500' : 'bg-surface-700'}`} />
      </div>

      <form onSubmit={handleSubmit}>
        {step === 1 && (
          <div className="space-y-4">
            {/* Company Name */}
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-surface-300 mb-2">
                Company Name
              </label>
              <div className="relative">
                <BuildingOfficeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Acme Corporation"
                  required
                  className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className="block text-sm font-medium text-surface-300 mb-2">
                Industry (Optional)
              </label>
              <select
                id="industry"
                name="industry"
                value={formData.industry}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              >
                {INDUSTRIES.map(ind => (
                  <option key={ind.value} value={ind.value}>{ind.label}</option>
                ))}
              </select>
            </div>

            {/* Timezone */}
            <div>
              <label htmlFor="timezone" className="block text-sm font-medium text-surface-300 mb-2">
                <ClockIcon className="w-4 h-4 inline mr-1" />
                Timezone
              </label>
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
              >
                {TIMEZONE_GROUPS.map(group => (
                  <optgroup key={group} label={group}>
                    {TIMEZONES.filter(tz => tz.group === group).map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="text-xs text-surface-500 mt-1">
                This timezone will be used for AI responses and date/time formatting
              </p>
            </div>
            
            {/* Data Warehouse Region */}
            <div>
              <label htmlFor="dataLocation" className="block text-sm font-medium text-surface-300 mb-2">
                <GlobeAltIcon className="w-4 h-4 inline mr-1" />
                Data Warehouse Region
              </label>
              {loadingRegions ? (
                <div className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : regions.length === 0 ? (
                <div className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-surface-400">
                  Default region will be used
                </div>
              ) : (
                <select
                  id="dataLocation"
                  name="dataLocation"
                  value={formData.dataLocation}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                >
                  {regions.map((region) => (
                    <option key={region.regionId} value={region.regionId}>
                      {region.flag} {region.name} ({region.regionId})
                      {region.isDefault ? ' - Default' : ''}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs text-surface-500 mt-1">
                Where your data will be stored. Cannot be changed after registration.
              </p>
              
              {/* Suggest Region Option */}
              <label className="flex items-center gap-2 mt-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={wantsSuggestRegion}
                  onChange={(e) => {
                    setWantsSuggestRegion(e.target.checked);
                    if (!e.target.checked) setSuggestedRegion('');
                  }}
                  className="w-4 h-4 rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-surface-400">
                  Don't see your region? Suggest one
                </span>
              </label>
              
              {wantsSuggestRegion && (
                <div className="mt-2">
                  <div className="relative">
                    <ChatBubbleLeftEllipsisIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                    <input
                      type="text"
                      value={suggestedRegion}
                      onChange={(e) => setSuggestedRegion(e.target.value)}
                      placeholder="e.g., Singapore, Brazil, Japan..."
                      className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <p className="text-xs text-surface-500 mt-1">
                    We'll create a feature request for this region. You'll use the default region for now.
                  </p>
                </div>
              )}
            </div>

            {/* Admin Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="adminFirstName" className="block text-sm font-medium text-surface-300 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                  <input
                    id="adminFirstName"
                    name="adminFirstName"
                    type="text"
                    value={formData.adminFirstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="adminLastName" className="block text-sm font-medium text-surface-300 mb-2">
                  Last Name
                </label>
                <input
                  id="adminLastName"
                  name="adminLastName"
                  type="text"
                  value={formData.adminLastName}
                  onChange={handleInputChange}
                  placeholder="Doe"
                  required
                  className="w-full px-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Admin Email */}
            <div>
              <label htmlFor="adminEmail" className="block text-sm font-medium text-surface-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <EnvelopeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-500" />
                <input
                  id="adminEmail"
                  name="adminEmail"
                  type="email"
                  value={formData.adminEmail}
                  onChange={handleInputChange}
                  placeholder="you@company.com"
                  required
                  className="w-full pl-11 pr-4 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                />
              </div>
              <p className="text-xs text-surface-500 mt-1">
                All account and billing notifications will be sent here
              </p>
            </div>

            {/* Invite Code (Required in Beta Mode) */}
            {betaMode && (
              <div>
                <label htmlFor="inviteCode" className="block text-sm font-medium text-surface-300 mb-2">
                  <TicketIcon className="w-4 h-4 inline mr-1" />
                  Invite Code <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <input
                    id="inviteCode"
                    name="inviteCode"
                    type="text"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                    placeholder="BETA-XXXXXXXX"
                    required
                    className={`w-full px-4 py-3 bg-surface-800 border rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 transition-all ${
                      inviteCodeValidation?.valid 
                        ? 'border-emerald-500 focus:ring-emerald-500' 
                        : inviteCodeValidation && !inviteCodeValidation.valid
                          ? 'border-red-500 focus:ring-red-500'
                          : 'border-surface-700 focus:ring-primary-500'
                    }`}
                  />
                  {isValidatingInviteCode && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                    </div>
                  )}
                  {!isValidatingInviteCode && inviteCodeValidation?.valid && (
                    <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                  )}
                  {!isValidatingInviteCode && inviteCodeValidation && !inviteCodeValidation.valid && (
                    <XMarkIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                  )}
                </div>
                {inviteCodeValidation?.valid && (
                  <div className="mt-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
                    <p className="text-sm text-purple-300 font-medium flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4" />
                      Welcome to the Beta!
                    </p>
                    <ul className="mt-2 text-xs text-surface-400 space-y-1">
                      <li className="flex items-center gap-1">
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                        Free Basic plan during beta period
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                        Permanent Founders Badge
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                        Basic: Free during beta, then 50% off for a year
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                        Upgrade during beta: $10 Pro / $25 Business first month
                      </li>
                      <li className="flex items-center gap-1">
                        <CheckIcon className="w-3 h-3 text-emerald-400" />
                        50% off that plan for the rest of your first year
                      </li>
                    </ul>
                    <p className="mt-2 text-xs text-amber-400 italic">
                      * Upgrade before the beta ends to lock in founders pricing!
                    </p>
                  </div>
                )}
                {inviteCodeValidation && !inviteCodeValidation.valid && (
                  <p className="text-xs text-red-400 mt-1">{inviteCodeValidation.message}</p>
                )}
              </div>
            )}

            {/* Referral Code */}
            <div>
              <label htmlFor="referralCode" className="block text-sm font-medium text-surface-300 mb-2">
                <GiftIcon className="w-4 h-4 inline mr-1" />
                Referral Code (Optional)
              </label>
              <div className="relative">
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="NEOBI-XXXXXXXX"
                  className={`w-full px-4 py-3 bg-surface-800 border rounded-lg text-white placeholder:text-surface-500 focus:outline-none focus:ring-2 transition-all ${
                    referralValidation?.valid 
                      ? 'border-emerald-500 focus:ring-emerald-500' 
                      : referralValidation && !referralValidation.valid
                        ? 'border-red-500 focus:ring-red-500'
                        : 'border-surface-700 focus:ring-primary-500'
                  }`}
                />
                {isValidatingReferral && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
                  </div>
                )}
                {!isValidatingReferral && referralValidation?.valid && (
                  <CheckIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-500" />
                )}
                {!isValidatingReferral && referralValidation && !referralValidation.valid && (
                  <XMarkIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {referralValidation?.valid && (
                <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1">
                  <GiftIcon className="w-3 h-3" />
                  You'll both get 250 credits when you make your first payment!
                </p>
              )}
              {referralValidation && !referralValidation.valid && (
                <p className="text-xs text-red-400 mt-1">{referralValidation.message}</p>
              )}
            </div>

            <button
              type="button"
              onClick={handleNextStep}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
            >
              Continue
              <ArrowRightIcon className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            {/* Beta Mode - Show beta perks confirmation */}
            {betaMode ? (
              <div className="space-y-4">
                <div className="p-4 bg-gradient-to-r from-purple-500/10 to-amber-500/10 border border-purple-500/30 rounded-xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center">
                      <span className="text-2xl">🏆</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-lg">Founder Status Confirmed</h3>
                      <p className="text-surface-400 text-sm">You're joining as an early supporter!</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-3 bg-surface-800/50 rounded-lg">
                      <CheckIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">Free Basic Plan During Beta</p>
                        <p className="text-surface-400 text-sm">Full access to Basic features until the beta ends</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-surface-800/50 rounded-lg">
                      <CheckIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">Permanent Founders Badge</p>
                        <p className="text-surface-400 text-sm">Displayed on your profile forever</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-3 bg-surface-800/50 rounded-lg">
                      <CheckIcon className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium">Founders Pricing on All Plans</p>
                        <p className="text-surface-400 text-sm">
                          Basic: Free during beta, then 50% off for your first year
                        </p>
                        <p className="text-surface-400 text-sm">
                          Pro/Business: $10/$25 first month, then 50% off for 11 months
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-amber-300 text-sm font-medium flex items-center gap-2">
                      <ClockIcon className="w-4 h-4" />
                      Important: Upgrade before the beta ends!
                    </p>
                    <p className="text-amber-200/70 text-xs mt-1">
                      Founders pricing is only available if you upgrade during the beta period. 
                      The discount is locked to the plan you choose (Basic, Pro or Business).
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* Normal Mode - Plan Selection */
              <div className="grid grid-cols-2 gap-3">
                {plans.map(plan => (
                  <PlanCard
                    key={plan.slug}
                    plan={plan}
                    isSelected={formData.planSlug === plan.slug}
                    onSelect={(slug) => setFormData(prev => ({ ...prev, planSlug: slug }))}
                  />
                ))}
              </div>
            )}

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer mt-4">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="w-5 h-5 rounded border-surface-600 bg-surface-800 text-primary-600 focus:ring-primary-500 mt-0.5"
              />
              <span className="text-sm text-surface-400">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary-400 hover:text-primary-300">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-primary-400 hover:text-primary-300">Privacy Policy</Link>
              </span>
            </label>

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handlePrevStep}
                className="flex-1 py-3 px-4 bg-surface-700 hover:bg-surface-600 text-white font-medium rounded-lg transition-all duration-200"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={isLoading || !acceptedTerms}
                className="flex-1 py-3 px-4 bg-primary-600 hover:bg-primary-500 text-white font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {betaMode ? 'Join Beta' : 'Create Account'}
                    <ArrowRightIcon className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </form>

      <p className="mt-6 text-center text-surface-400 text-sm">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors">
          Sign in
        </Link>
      </p>
    </div>
  );
}
